import {IconButton, Separator} from 'platform/components';
import {Box, HStack, Space, Text} from 'platform/foundation';
import styled from 'styled-components';

import {ReactNode} from 'react';

import {prop} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DASHBOARD_ROW_UNIT} from '../../consts/dashboardRowUnit';
import {DASHBOARD_TILE_SIZES} from '../../consts/dashboardTileSizes';
import {DashboardHiddenTile} from '../../types/DashboardHiddenTile';
import {DashboardTileId} from '../../types/DashboardTileId';
import {DashboardTileSizeShape} from '../DashboardGrid/DashboardTileSizeShape';

interface DashboardHiddenTilePreviewProps extends TestIdProps {
  tile: DashboardHiddenTile;
  onShow: (id: DashboardTileId) => void;
  children: ReactNode;
}

const PREVIEW_SCALE = 0.4;
// Space units of one virtual grid column: 80 × 4px = 320px, roughly one quarter column of the
// desktop grid, so the tile body lays out as it would on the dashboard before being scaled down.
const PREVIEW_VIRTUAL_WIDTH_UNITS = 80;

// 1px hairline: theme.getSize has no sub-unit step; a 4px dashed border overwhelms the 0.4x preview.
const StyledPreviewCard = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px dashed ${({theme}) => theme.colors.palettes.neutral[70][100]};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.general.white};
`;

// Crop the tile's own bottom padding: 16px on the tile becomes 6.4px after scaling.
const StyledScaledFrame = styled.div<{$columns: number; $rows: number}>`
  position: relative;
  overflow: hidden;
  width: calc(
    ${({theme}) => theme.getSize(PREVIEW_VIRTUAL_WIDTH_UNITS)} * ${({$columns}) => $columns} *
      ${PREVIEW_SCALE}
  );
  height: calc(
    ${DASHBOARD_ROW_UNIT} * ${({$rows}) => $rows} *
      ${PREVIEW_SCALE} - calc(${({theme}) => theme.getSize(4)} * ${PREVIEW_SCALE})
  );
`;

const StyledScaledContent = styled.div<{$columns: number; $rows: number}>`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(
    ${({theme}) => theme.getSize(PREVIEW_VIRTUAL_WIDTH_UNITS)} * ${({$columns}) => $columns}
  );
  height: calc(${DASHBOARD_ROW_UNIT} * ${({$rows}) => $rows});
  transform: scale(${PREVIEW_SCALE});
  transform-origin: top left;
  pointer-events: none;
  user-select: none;
`;

export function DashboardHiddenTilePreview(props: DashboardHiddenTilePreviewProps) {
  const {columns, rows} = prop(props.tile.previewSize, DASHBOARD_TILE_SIZES);

  return (
    <StyledPreviewCard>
      <StyledScaledFrame $columns={columns} $rows={rows}>
        {/* The preview is a picture of the tile: `inert` keeps its controls out of the tab order
            and away from pointer input, `aria-hidden` keeps the mock data from assistive tech. */}
        <StyledScaledContent $columns={columns} $rows={rows} inert aria-hidden>
          {props.children}
        </StyledScaledContent>
      </StyledScaledFrame>
      <Separator spacing={0} />
      <Box paddingVertical={1} paddingHorizontal={2}>
        <HStack spacing={2} align="center">
          <DashboardTileSizeShape size={props.tile.previewSize} />
          <Text size="xSmall" color="secondary">
            {props.tile.label}
          </Text>
          <Space fillAvailable />
          <IconButton
            icon="action/visibility"
            size="small"
            aria-label={`${i18n.t('page.homepage.dashboardLayout.showTile')}: ${props.tile.label}`}
            onClick={() => props.onShow(props.tile.id)}
            data-testid={suffixTestId(`hiddenTile-${props.tile.id}`, props)}
          />
        </HStack>
      </Box>
    </StyledPreviewCard>
  );
}
